import {
  ElementRef,
  Injectable,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject, Subject } from 'rxjs';

import {
  CK5CommentMarkerTypeClass,
  CommentSequence,
  CommentTypeEnum,
  CustomCommentThread,
  ISideBarView,
  ISuggestion,
  Range,
} from '../model';
import { CommonConstants } from '../shared';
import { AppStore } from '../store/states/app.state';
import { setSelectedCommentsTabIndex } from '../modules/wk-document-decorator';
import { CitationService } from './citations.service';
import * as _ from 'lodash';
import { RHP_EDITOR_ID } from '../modules/wk-document-decorator/const';

const {
  CK_PLUGINS: { ANNOTATION_UIS, TRACK_CHANGES_EDITING, COMMENTS_REPOSITORY },
  commands: { ADD_COMMENT_THREAD },
  comments: { ckCommentWrapper, dataThreadId, wideSidebar, dataComment },
  ckEditorEditablePlaceholder,
  rel,
  BOX_COMMENTS,
  dataCommentActiveClass,
  SKOS_EDITORIAL_NOTE,
  ID_ATTR,
  dataSuggestionDeletion,
  trackChanges: { ckSuggestionWrapper, ckSuggestionWrapperActive },
} = CommonConstants;

interface CommentSuggestion {
  previous?: unknown;
}

@Injectable({ providedIn: 'root' })
export class WkCommentsTabService {
  createdReleaseNoteId = '';
  readonly contentChangedSubject = new Subject<void>();
  readonly contentChanged$ = this.contentChangedSubject.asObservable();
  readonly editorInstanceChangedSubject = new ReplaySubject<{
    editableElement: HTMLElement;
    suggestions: { id: string }[];
  }>(1);
  private readonly CommentThreadId =
    CommonConstants.comments.dataCommentThreadId;
  readonly editorInstanceChanged$ =
    this.editorInstanceChangedSubject.asObservable();
  commentElements: string[] = [];
  public releaseNoteClicked: string;
  public idThreadMsg: string;
  public addCommentWithCloseSidebar = false;
  public isReleaseNoteAdded = false;
  public newCommentThreadAddedId: string;
  public firstPositionOfSelection = null;
  public lastPositionOfSelection = null;
  public isCommentsTabOpened = false;
  public threadIdToFocus: string | null = null;
  /** True while creating a comment thread that must be focused after rendering. */
  public isCreatingCommentThread = false;
  /** True while execute(ADD_COMMENT_THREAD) is running — used to detect the re-entrant
   *  addCommentThread event that CKEditor fires synchronously inside that execute call. */
  public addCommentThreadExecuting = false;
  private readonly toggleSubject = new Subject<boolean>();
  public toggle$ = this.toggleSubject.asObservable();
  public isInlineToggled = false;
  private observer: MutationObserver | null = null;
  private readonly renderer: Renderer2;
  /** Tab (Editorial or Release Notes) that the pending creation must open. */
  public creationTabIndex: number | null = null;
  constructor(
    private readonly citationService: CitationService,
    rendererFactory: RendererFactory2,
    private readonly store: Store<AppStore>
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  public dispatchSelectedCommentsTabIndex(index: number): void {
    this.store.dispatch(setSelectedCommentsTabIndex({ index }));
  }

  getCommentSequence(
    attr: CommentTypeEnum,
    editableElement: HTMLElement,
    suggestions = [],
    editorInstance
  ): CommentSequence {
    // Ignore one node per 'replace' operation for correct ordering
    suggestions = suggestions.filter(
      (p: CommentSuggestion) => !Boolean(p.previous)
    );

    this.commentElements = [];
    const editingRootDomElement =
      editorInstance.editing.view.domRoots.get('main');
    this.commentElements = editingRootDomElement?.querySelectorAll(
      `.ck-comment-marker,[${this.CommentThreadId}]`
    );

    const suggestionElements = editableElement
      ? Array.from(
          editableElement.querySelectorAll(CK5CommentMarkerTypeClass.SUGGESTION)
        )
      : [];

    const threads = new Map<string, any>([
      [CommentTypeEnum.COMMENT, this.commentElements],
      [
        CommentTypeEnum.RELEASENOTES,
        Array.from(
          editingRootDomElement.querySelectorAll(
            `[rel="${SKOS_EDITORIAL_NOTE}"]`
          )
        ) ?? [],
      ],
      [CommentTypeEnum.SUGGESTION, suggestionElements],
    ]);

    const comments = threads.get(attr);
    const sequence: CommentSequence = [];

    Object.entries(comments).forEach(
      ([commentIndex, comment]: [string, HTMLElement]) => {
        const order = Number(commentIndex);
        const threadId = new Map<string, string>([
          [
            CommentTypeEnum.COMMENT,
            comment.dataset[attr] || comment.dataset['commentThreadId'],
          ],
          [CommentTypeEnum.RELEASENOTES, comment.getAttribute('id')],
          [CommentTypeEnum.SUGGESTION, comment.dataset[attr]],
        ]);
        const commentId = threadId.get(attr) ?? '';
        const match = suggestions.find(({ id }) => id === commentId);

        if (match && attr === CommentTypeEnum.SUGGESTION) {
          sequence.push([order, commentId]);
        }

        if (
          attr === CommentTypeEnum.COMMENT ||
          attr === CommentTypeEnum.RELEASENOTES
        ) {
          sequence.push([order, commentId]);
        }
      }
    );
    this.setSequenceBasedOnIndex(sequence);
    return sequence.sort((seqA, seqB) => seqA[0] - seqB[0]);
  }

  private setSequenceBasedOnIndex(sequence) {
    for (let j = 0; j < sequence.length; j++) {
      sequence[j][0] = sequence[j][0] !== j ? j : sequence[j][0];
    }
  }

  /**
   * Places cursor inside new comment thread opened
   * @private
   * @param {string} threadActiveId
   * @returns {void}
   */
  private onInsertCommentThread(threadActiveId?: string): void {
    const targetThreadId = threadActiveId || this.createdReleaseNoteId;
    const sidebarItems =
      this.citationService.ckeditorInstanceFromEditorComp?.plugins
        ?.get(ANNOTATION_UIS)
        ?._uisData?.get(wideSidebar)?.collection;
    const sidebarItem = sidebarItems?.find((item: ISideBarView) =>
      item.view.element.querySelector(`[${dataThreadId}="${targetThreadId}"]`)
    );
    if (sidebarItem) {
      const { element } = sidebarItem.view.mainView;
      const threadInEditMode = sidebarItem.view.element.querySelector(
        ckEditorEditablePlaceholder
      );

      const isThreadEditorAlreadyFocused =
        threadInEditMode?.classList?.contains('ck-focused') ||
        (threadInEditMode != null &&
          document.activeElement === threadInEditMode);

      if (!isThreadEditorAlreadyFocused) {
        const focusTarget = threadInEditMode || element;
        (focusTarget as HTMLElement).focus({ preventScroll: true });
      }
    }
  }

  private isSidebarActive(): boolean {
    return Boolean(
      this.citationService.ckeditorInstanceFromEditorComp?.plugins
        ?.get(ANNOTATION_UIS)
        ?.activeUIs?.has(wideSidebar)
    );
  }

  /**
   * Focus / Highlight comment thread in right hand panel
   * @public
   * @param { string } threadActiveId
   * @param { ElementRef } elementRef
   */
  public onCommentReadyForFocus(
    threadActiveId: string,
    elementRef: ElementRef
  ): void {
    const shouldFocusThread =
      this.isCreatingCommentThread || this.threadIdToFocus === threadActiveId;
    if (this.isCreatingCommentThread && this.creationTabIndex !== null) {
      this.dispatchSelectedCommentsTabIndex(this.creationTabIndex);
    }
    if (shouldFocusThread && !this.isInlineToggled && !this.isSidebarActive()) {
      if (!this.isReleaseNoteAdded) {
        this.idThreadMsg = null;
        this.citationService.isOpenCommentsTab.next(true);
      }
      // Activation can be synchronous; wait only if the sidebar is still inactive.
      if (!this.isSidebarActive()) {
        return;
      }
    }
    const isCommentsTabNotAvailble =
      this.addCommentWithCloseSidebar ||
      this.newCommentThreadAddedId !== threadActiveId;
    const thread = elementRef.nativeElement.querySelector(`.${BOX_COMMENTS}`);
    const suggestionThread = elementRef.nativeElement.querySelector(
      `[data-suggestion-id="${threadActiveId}"], .${ckSuggestionWrapperActive}`
    );
    const isFormattingTypeSuggestion =
      this.citationService.ckeditorInstanceFromEditorComp?.plugins
        .get(TRACK_CHANGES_EDITING)
        .getSuggestions()
        .find(
          (suggestion: ISuggestion) => suggestion.subType === threadActiveId
        );
    /* Place cursor position in comment thread editor upon thread creation.
     * Skip for suggestion/track-change annotations — they have no reply input and
     * calling onInsertCommentThread for them would steal focus from the document
     * editor while the user is editing tracked changes (CTA-45914).
     * Skip too for plain document clicks on an existing editorial comment — only
     * focus the reply box while a new thread is being created, so single/double
     * clicks keep the cursor in the document body (CTA-45960). */
    if (
      threadActiveId &&
      !suggestionThread &&
      !isFormattingTypeSuggestion &&
      shouldFocusThread
    ) {
      this.idThreadMsg = null;
      this.onInsertCommentThread(threadActiveId);
      this.threadIdToFocus = null;
    } else if (suggestionThread || isFormattingTypeSuggestion) {
      this.threadIdToFocus = null;
    }
    /* Create/Focus comment thread or release note thread */
    if (
      threadActiveId &&
      isCommentsTabNotAvailble &&
      thread &&
      !suggestionThread &&
      !isFormattingTypeSuggestion
    ) {
      const threadId = this.isReleaseNoteAdded
        ? { threadId: this.createdReleaseNoteId }
        : { threadId: threadActiveId };
      /* Open input when thread exists but has no editor yet; focus existing editor otherwise */
      const threadEl = elementRef.nativeElement.querySelector(
        `[data-thread-id="${threadActiveId}"]`
      );
      const threadInputOpen = threadEl?.querySelector(
        ckEditorEditablePlaceholder
      );
      if (threadEl && threadInputOpen && this.isCreatingCommentThread) {
        this.onInsertCommentThread(threadActiveId);
      } else if (this.isCreatingCommentThread || this.isReleaseNoteAdded) {
        // Restore the stored selection so ADD_COMMENT_THREAD marks the correct text,
        // even if the model selection was lost (e.g. focus moved to the sidebar panel).
        if (this.firstPositionOfSelection && this.lastPositionOfSelection) {
          const { model } = this.citationService.ckeditorInstanceFromEditorComp;
          model.change((writer) => {
            writer.setSelection(
              writer.createRange(
                this.firstPositionOfSelection,
                this.lastPositionOfSelection
              )
            );
          });
        }
        this.addCommentThreadExecuting = true;
        this.citationService.ckeditorInstanceFromEditorComp?.execute(
          ADD_COMMENT_THREAD,
          threadId
        );
        this.addCommentThreadExecuting = false;
        const focusThreadId = threadId.threadId;
        const tryFocusThread = (attempt: number) => {
          // Query ALL matching thread editable elements and pick the visible one
          const allThreadEditables: HTMLElement[] = Array.from(
            elementRef.nativeElement.querySelectorAll(
              `[data-thread-id="${focusThreadId}"] ${ckEditorEditablePlaceholder}`
            )
          ).filter((el): el is HTMLElement => el instanceof HTMLElement);
          const threadEditable = allThreadEditables.find(
            (el: HTMLElement) =>
              el.offsetParent != null || el.getBoundingClientRect().width > 0
          );
          if (threadEditable) {
            threadEditable.focus({ preventScroll: true });
          } else if (attempt < 15) {
            setTimeout(() => tryFocusThread(attempt + 1), 500);
          }
        };
        setTimeout(() => tryFocusThread(1), 500);
      }
      this.isReleaseNoteAdded = false;
      this.newCommentThreadAddedId = threadActiveId;
    }
    elementRef.nativeElement
      .querySelector(`[data-thread-id="${threadActiveId}"]`)
      ?.querySelector(`.${ckCommentWrapper}, .${ckSuggestionWrapper}`) &&
      this.citationService.idToFocus.next(threadActiveId);
    this.isCommentsTabOpened = true;
    this.isCreatingCommentThread = false;
    this.creationTabIndex = null;
    this.firstPositionOfSelection = null;
    this.lastPositionOfSelection = null;
  }

  /**
   *
   * @param range This method removes release note in the specified range
   * @returns {void}
   */
  public removeSelectedReleaseNotes(subTemplateRange?: Range): void {
    if (this.citationService.ckeditorInstanceFromEditorComp) {
      const { model, editing, plugins } =
        this.citationService.ckeditorInstanceFromEditorComp;
      const range =
        subTemplateRange || model?.createRangeIn(model?.document?.getRoot());
      const domRoot = editing?.view?.domRoots?.get('main');

      if (domRoot && range) {
        const releaseNoteIds: string[] = !subTemplateRange
          ? [...domRoot.querySelectorAll(`[rel="${SKOS_EDITORIAL_NOTE}"]`)]
              .filter(
                (node) =>
                  node.classList?.contains(dataCommentActiveClass) ||
                  node.classList?.contains(dataSuggestionDeletion)
              )
              .map((ref) => ref?.getAttribute('id'))
          : [...domRoot.querySelectorAll(`[rel="${SKOS_EDITORIAL_NOTE}"]`)].map(
              (releaseNoteElement) => releaseNoteElement.id
            );

        const releaseNotes: Element[] = _.filter(
          [...range.getItems()],
          (ele) =>
            ele?.getAttribute?.(ID_ATTR) &&
            _.includes(releaseNoteIds, ele.getAttribute(ID_ATTR))
        );
        _.forEach(releaseNotes, (releaseNote: Element) => {
          model.enqueueChange({ isUndoable: false }, (writer) => {
            writer?.remove(releaseNote);

            const commentThreads =
              plugins?.get(COMMENTS_REPOSITORY)?.getCommentThreads?.({
                skipNotAttached: true,
                toJSON: true,
                skipEmpty: true,
              }) || [];

            commentThreads.forEach((commentThread: CustomCommentThread) => {
              if (commentThread?.id === releaseNote.getAttribute?.(ID_ATTR)) {
                commentThread.remove();
              }
            });
          });
        });
      }
    }
  }

  /**
   *
   * @param range This method removes comments in the specified range
   * @returns {void}
   */
  public removeCommentsInRange(range: Range): void {
    if (this.citationService?.ckeditorInstanceFromEditorComp) {
      const { editing, plugins } =
        this.citationService.ckeditorInstanceFromEditorComp;
      const viewRange = editing?.mapper?.toViewRange?.(range);
      const domRange =
        viewRange && editing?.view?.domConverter?.viewRangeToDom?.(viewRange);
      const commentsAndReleaseNotes = domRange?.commonAncestorContainer
        ? [
            ...domRange.commonAncestorContainer.querySelectorAll(
              `[${dataComment}]`
            ),
          ]
        : [];
      const commentElementsIds: string[] = _.filter(
        _.map(
          _.filter(
            commentsAndReleaseNotes,
            (el) => el?.getAttribute?.(rel) !== SKOS_EDITORIAL_NOTE
          ),
          (commentElement) => commentElement?.getAttribute?.(dataComment)
        ),
        (id): id is string => !!id
      );

      const commentThreads =
        plugins?.get(COMMENTS_REPOSITORY)?.getCommentThreads?.({
          skipNotAttached: true,
          toJSON: true,
          skipEmpty: true,
        }) || [];

      _.forEach(commentElementsIds, (threadId) => {
        const thread = _.find(commentThreads, (t) => t?.id === threadId);
        thread?.remove();
      });
    }
  }

  public setToggle(value: boolean): void {
    this.toggleSubject.next(value);
  }

  /**
   * Sets up a MutationObserver to monitor changes within the `.ck-annotation-wrapper` element.
   *
   * This function is specifically designed for use inline annotations or comment threads.
   * It observes DOM mutations and dynamically toggles the visibility
   * of the surrounding `.ck-balloon-panel` based on the presence of a thread element.
   *
   * Use Case:
   * - When a comment thread (`.ck-thread`) is rendered inside the annotation wrapper, the balloon panel
   *   should be hidden to prevent UI overlap or redundancy.
   * - If trackchange thread and no comment thread is present, the balloon panel remains visible to allow annotation interactions.
   * - If selection is inside RHP CKEditor EditorInstance return. (RHP editorInstance will have `rhp-ckeditor-instance - id` or `editor-instance - classname` on ckeditor selector.)
   */
  public observeAnnotationWrapper(): void {
    this.observer = new MutationObserver(() => {
      try {
        const selection = document.getSelection();
        const rhpCkeditorAncestor = this.getAncestorUntilCkeditor(selection);

        if (rhpCkeditorAncestor) {
          return;
        }

        const wrapper = document.querySelector('.ck-annotation-wrapper');
        const firstChild = wrapper?.firstChild as HTMLElement | null;
        const panelStyleValue = firstChild?.className.includes('ck-thread')
          ? 'none'
          : '';
        const panel = wrapper?.closest('.ck-balloon-panel');
        const panelNextSib = panel?.nextSibling as HTMLElement | null;
        if (!(panel instanceof HTMLElement)) {
          return;
        }
        if (this.isInlineToggled) {
          this.renderer.setStyle(panel, 'display', panelStyleValue, 2);
        } else {
          this.renderer.setStyle(panel, 'display', panelStyleValue, 2);
          if (
            panelStyleValue === 'none' &&
            panelNextSib?.classList?.contains?.('ck-fake-panel')
          ) {
            this.renderer.setStyle(panelNextSib, 'display', 'none', 2);
          }
        }
      } catch (error) {
        console.error('Error in ck-balloon-panel observer:', error);
      }
    });

    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Disconnects the MutationObserver if active and clears its reference.
   */
  public disconnect(): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  private getAncestorUntilCkeditor(
    selection: Selection | null
  ): HTMLElement | null {
    if (!selection || !selection.anchorNode) {
      return null;
    }

    let node: HTMLElement | null =
      selection.anchorNode.nodeType === Node.TEXT_NODE
        ? selection.anchorNode.parentElement
        : (selection.anchorNode as HTMLElement);

    while (node) {
      // Stop if we reach the RHP CKEditor instance
      if (
        node.id === RHP_EDITOR_ID ||
        node.classList.contains('editor-instance')
      ) {
        return node;
      }
      node = node.parentElement;
    }

    return null; // Not inside RHP CKEditor
  }
}
